import { useEffect } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import "bootstrap/dist/css/bootstrap.min.css";
import "./aboutus.css";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import { NavLink, useLocation  } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0 },
};

const AboutUs = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth"
    });
  }, [pathname]);

  return (
    <div className="about-wrapper">
      {/* header */}
      <Header />

      {/* ================= HERO SECTION ================= */}
      <section className="hero-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            variants={fadeUp}
            className="text-center"
          >
            <h1 className="hero-title">
              Redefining Premium Technology Experience
            </h1>

            <p className="hero-text">
              At Crescent World, we bring you the latest and most advanced Apple
              devices — from powerful iPhones to ultra-sleek MacBooks and smart
              iWatches. We are committed to delivering authentic products,
              unmatched service, and a seamless shopping experience.
            </p>

            <p className="hero-highlight">
              We don’t just sell devices. We deliver innovation, performance,
              and elegance.
            </p>

            <Button as={NavLink} to="/shop" className="gradient-btn mt-4">
              Explore Collection
            </Button>
          </motion.div>
        </Container>
      </section>

      {/* ================= WHO WE ARE ================= */}
      <section className="section-light">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                transition={{ duration: 0.8 }}
                variants={fadeUp}
              >
                <h2 className="section-title">Who We Are</h2>
                <p>
                  Crescent World is a modern technology-focused e-commerce
                  platform dedicated to providing genuine Apple products at
                  competitive prices.
                </p>

                <ul className="custom-list">
                  <li>Offer 100% authentic devices</li>
                  <li>Provide transparent pricing</li>
                  <li>Deliver products safely and quickly</li>
                  <li>Ensure customer satisfaction</li>
                </ul>

                <p>
                  We serve tech enthusiasts, professionals, students, and
                  businesses who demand quality and performance.
                </p>
              </motion.div>
            </Col>

            <Col lg={6}>
              <motion.img
                src="https://images.unsplash.com/photo-1517336714731-489689fd1ca8"
                alt="Apple Devices"
                className="section-image"
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1 }}
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ================= WHAT WE OFFER ================= */}
      <section className="section-light-alt">
        <Container>
          <h2 className="section-title text-center mb-5">What We Offer</h2>

          <Row className="g-4">
            {[
              "iPhone Collection",
              "MacBook Series",
              "Apple Watch",
              "iPad Range",
            ].map((item, index) => (
              <Col md={6} lg={3} key={index}>
                <motion.div
                  className="offer-card"
                  whileHover={{ y: -10 }}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: index * 0.2 }}
                >
                  <h5>{item}</h5>
                  <p>
                    Experience unmatched performance, seamless ecosystem
                    integration, and elegant design.
                  </p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ================= WHY CHOOSE US ================= */}
      <section className="section-light">
        <Container>
          <h2 className="section-title text-center mb-5">
            Why Choose Crescent World
          </h2>

          <Row className="g-4">
            {[
              "100% Genuine Products",
              "Fast & Secure Delivery",
              "Secure Payments",
              "Dedicated Support",
              "Premium Experience",
            ].map((item, i) => (
              <Col md={6} lg={4} key={i}>
                <motion.div
                  className="feature-box"
                  whileHover={{ scale: 1.05 }}
                >
                  {item}
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ================= COUNTER SECTION ================= */}
      <section className="counter-section">
        <Container>
          <Row className="text-center">
            <Col md={3}>
              <h2>
                <CountUp end={10000} duration={3} />+
              </h2>
              <p>Happy Customers</p>
            </Col>
            <Col md={3}>
              <h2>
                <CountUp end={5000} duration={3} />+
              </h2>
              <p>Orders Delivered</p>
            </Col>
            <Col md={3}>
              <h2>100%</h2>
              <p>Genuine Products</p>
            </Col>
            <Col md={3}>
              <h2>4.9★</h2>
              <p>Customer Rating</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ================= MISSION & VISION ================= */}
      <section className="section-light-alt">
        <Container>
          <Row>
            <Col md={6}>
              <h3>Our Mission</h3>
              <p>
                Here is a strong, premium 100-word mission statement for
                Crescent World:- At Crescent World, our mission is to make
                premium technology accessible, reliable, and inspiring for
                everyone. We are committed to delivering 100% genuine Apple
                products with transparent pricing and a seamless shopping
                experience. Our focus goes beyond selling devices - we aim to
                empower creativity, productivity, and meaningful connections
                through innovative technology. By combining trusted sourcing,
                secure transactions, and fast delivery, we ensure every customer
                receives exceptional value and service. We strive to build
                long-term relationships based on trust, integrity, and
                excellence, creating a technology destination where quality,
                performance, and customer satisfaction always come first.
              </p>
            </Col>

            <Col md={6}>
              <h3>Our Vision</h3>
              <p>
                Here is a strong, premium 100-word vision statement for Crescent
                World:- At Crescent World, our vision is to become a trusted and
                leading destination for premium Apple technology. We aim to
                build a brand recognized for authenticity, innovation, and
                exceptional customer experience. By consistently delivering
                genuine products, transparent pricing, and reliable service, we
                strive to create long-term relationships rooted in trust and
                satisfaction. We envision a growing community of tech
                enthusiasts, professionals, and creators who rely on us for
                quality and performance. Through continuous improvement and
                customer-focused values, we seek to redefine online technology
                retail and set new standards for excellence in the digital
                marketplace.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ================= CTA ================= */}
      <section className="cta-section text-center">
        <h2>Ready to Experience Premium Technology?</h2>
        <p>
          Explore our collection and discover the perfect Apple device for your
          lifestyle.
        </p>

        <Button className="gradient-btn me-3">Shop Now</Button>
        <Button className="outline-btn">View Offers</Button>
      </section>

      {/* footer */}
      <Footer />
    </div>
  );
};

export default AboutUs;
